/**
 * Tiny typed client for the inspire-auth Worker. The Worker lives at
 * `inspire.optymyse.com/api/identity/*` (same origin as this Pages
 * deploy, so no CORS), and every request is credentialed so the
 * Worker can set the `inspire-session` cookie on successful auth.
 *
 * Failure model (see ApiError below): every non-2xx response plus
 * every network-level rejection is normalised into a typed ApiError
 * with a `kind` discriminant. Screens render a polished error card
 * with microcopy that matches the kind — "too many attempts, try
 * again in 30s" reads as human, "Something went wrong" does not. The
 * `requestId` + `code` make support tickets grep-able in Worker logs.
 */

/**
 * Tenant branding the Worker resolved for this email. Present only when
 * the email maps to exactly one tenant in one region with a logo set.
 * Cross-faded onto the password step's shell. See LOGIN-SECURITY.md for
 * the enumeration trade-off this represents (deliberate, M365-shaped).
 */
export interface LocateBranding {
    tenantId: string;
    tenantName: string;
    /** Same-origin path (no host prefix) so the CDN/cache rules apply. */
    logoUrl: string;
}

export interface LocateResponseOk {
    locateToken: string;
    orgPicker: boolean;
    orgChoices?: { region: string }[];
    branding?: LocateBranding;
}

/**
 * Worker is gating further locates on a Cloudflare Turnstile challenge.
 * Triggered after enough decoy outcomes from this client IP within a
 * sliding window. The browser renders the widget bound to siteKey, then
 * resubmits the locate with the resulting token.
 */
export interface LocateResponseChallenge {
    challenge: "turnstile";
    siteKey: string;
}

export type LocateResponse = LocateResponseOk | LocateResponseChallenge;

export function isChallengeResponse(
    response: LocateResponse,
): response is LocateResponseChallenge {
    return "challenge" in response;
}

export interface AuthenticateResponseOk {
    ok: true;
    mfaRequired?: false;
    mustChangePassword: boolean;
    displayName: string | null;
}

export interface AuthenticateResponseMfa {
    ok: true;
    mfaRequired: true;
    mfaToken: string;
}

export interface AuthenticateResponseFail {
    ok: false;
}

export type AuthenticateResponse =
    | AuthenticateResponseOk
    | AuthenticateResponseMfa
    | AuthenticateResponseFail;

export interface MfaVerifyResponse {
    ok: boolean;
}

// ─── ApiError — typed failure surface ────────────────────────────
//
// `kind` drives the UI's microcopy + iconography + whether to retry.
// `code` is the short token shown to the user (e.g. `INS-NET`) so
// support can grep for it. `requestId` is the CF `cf-ray` from the
// response headers, also user-visible, so support can find the exact
// log line in `wrangler tail`. `retryAfterSeconds` is only set on
// `rate-limit` and drives the live countdown in the UI.

export type ApiErrorKind =
    | "network"       // navigator offline OR fetch() threw
    | "rate-limit"    // 429 with Retry-After
    | "server"        // 5xx
    | "bad-request"   // 4xx other than 401/403/429
    | "unauthorized"  // 401 — used by admin flows to redirect
    | "forbidden";    // 403 — used by admin flows to show "no access"

export class ApiError extends Error {
    readonly kind: ApiErrorKind;
    readonly code: string;
    readonly requestId: string | null;
    readonly retryAfterSeconds: number | null;
    readonly status: number;

    constructor(init: {
        kind: ApiErrorKind;
        code: string;
        message: string;
        requestId: string | null;
        retryAfterSeconds?: number | null;
        status?: number;
    }) {
        super(init.message);
        this.kind = init.kind;
        this.code = init.code;
        this.requestId = init.requestId;
        this.retryAfterSeconds = init.retryAfterSeconds ?? null;
        this.status = init.status ?? 0;
    }
}

/**
 * Format a short user-facing reference so support can find this
 * request in `wrangler tail` logs. Shown in fine print under the
 * error message. Stripe/Vercel do the same — opaque to the user,
 * load-bearing for support tickets.
 */
export function formatSupportRef(err: ApiError): string {
    return err.requestId
        ? `${err.code} · ${err.requestId}`
        : err.code;
}

function extractRequestId(response: Response): string | null {
    return response.headers.get("cf-ray") ?? response.headers.get("x-request-id");
}

function parseRetryAfter(value: string | null): number | null {
    if (!value) return null;
    const seconds = Number.parseInt(value, 10);
    if (!Number.isNaN(seconds) && seconds > 0) return seconds;
    // Retry-After can also be an HTTP-date. Convert to seconds from
    // now, floored at 0 so a past date doesn't surface as "wait -2s".
    const when = Date.parse(value);
    if (!Number.isNaN(when)) {
        return Math.max(0, Math.ceil((when - Date.now()) / 1000));
    }
    return null;
}

async function classifyResponse(response: Response, opName: string): Promise<ApiError> {
    const requestId = extractRequestId(response);
    const status = response.status;
    if (status === 429) {
        return new ApiError({
            kind: "rate-limit",
            code: "INS-429",
            message: "Too many attempts. Please wait a moment before trying again.",
            requestId,
            retryAfterSeconds: parseRetryAfter(response.headers.get("retry-after")) ?? 60,
            status,
        });
    }
    if (status === 401) {
        return new ApiError({
            kind: "unauthorized",
            code: "INS-401",
            message: "Your session has ended. Please sign in again.",
            requestId,
            status,
        });
    }
    if (status === 403) {
        return new ApiError({
            kind: "forbidden",
            code: "INS-403",
            message: "You don't have access to this.",
            requestId,
            status,
        });
    }
    if (status >= 500) {
        return new ApiError({
            kind: "server",
            code: `INS-${status}`,
            message: "The service is having trouble right now. Please try again shortly.",
            requestId,
            status,
        });
    }
    return new ApiError({
        kind: "bad-request",
        code: `INS-${status}`,
        message: `Couldn't ${opName}. Please try again.`,
        requestId,
        status,
    });
}

/**
 * Turn a fetch rejection into a network ApiError. Also covers the
 * "navigator.onLine is false" case — UA may still attempt the fetch
 * (and reject) but bubbling the offline fact up lets us show a
 * different message ("You're offline") vs a connectivity blip
 * ("Can't reach the service").
 */
function classifyThrown(err: unknown, opName: string): ApiError {
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    const message = offline
        ? "You're offline. Reconnect and try again."
        : `Can't reach the sign-in service. Check your connection and try again.`;
    const code = offline ? "INS-NET-OFF" : "INS-NET";
    return new ApiError({
        kind: "network",
        code,
        message,
        requestId: null,
        status: 0,
    });
}

async function postJson<T>(path: string, body: unknown, opName: string): Promise<T> {
    let response: Response;
    try {
        response = await fetch(path, {
            method: "POST",
            credentials: "same-origin",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(body),
        });
    } catch (err) {
        throw classifyThrown(err, opName);
    }
    if (response.ok) {
        return (await response.json()) as T;
    }
    // 401/400 from the identity endpoints are expected — they carry a
    // structured JSON body with `ok: false` that callers pattern-match
    // on. Anything else (rate-limit, 5xx, forbidden) is classified into
    // an ApiError so the UI can render category-specific microcopy.
    if (response.status === 400 || response.status === 401) {
        return (await response.json()) as T;
    }
    throw await classifyResponse(response, opName);
}

export async function locate(
    email: string,
    turnstileToken?: string,
): Promise<LocateResponse> {
    const body: { email: string; turnstileToken?: string } = { email };
    if (turnstileToken) body.turnstileToken = turnstileToken;
    return postJson<LocateResponse>("/api/identity/locate", body, "look you up");
}

export async function authenticate(
    locateToken: string,
    password: string,
    region?: string,
): Promise<AuthenticateResponse> {
    return postJson<AuthenticateResponse>(
        "/api/identity/authenticate",
        { locateToken, password, region },
        "sign you in",
    );
}

export async function verifyMfa(mfaToken: string, code: string): Promise<MfaVerifyResponse> {
    return postJson<MfaVerifyResponse>(
        "/api/identity/mfa/verify",
        { mfaToken, code },
        "verify your code",
    );
}

export interface ForgotPasswordResponse {
    ok: true; // Worker always returns success (anti-enumeration)
}

export async function forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    return postJson<ForgotPasswordResponse>(
        "/api/identity/forgot-password",
        { email },
        "send the reset email",
    );
}

export type FailureCode = "invalid" | "weak_password" | "already_accepted";

export interface CompletionSuccess {
    ok: true;
    displayName: string | null;
}

export interface CompletionFailure {
    ok: false;
    failureCode: FailureCode;
    errors?: string[];
}

export type CompletionResponse = CompletionSuccess | CompletionFailure;

export async function resetPassword(
    email: string,
    token: string,
    newPassword: string,
    region?: string,
): Promise<CompletionResponse> {
    return postJson<CompletionResponse>(
        "/api/identity/reset-password",
        { email, token, newPassword, region },
        "reset your password",
    );
}

export async function register(
    email: string,
    inviteToken: string,
    password: string,
    region?: string,
): Promise<CompletionResponse> {
    return postJson<CompletionResponse>(
        "/api/identity/register",
        { email, inviteToken, password, region },
        "complete your invitation",
    );
}

// Federated admin UI moved to the Director SPA — see
// Edge.Client/src/pages/platform/admin/* and Edge.Client/src/lib/admin-api.ts.
// This bundle is the unauthenticated front door only (sign-in,
// forgot-password, accept-invite).
